import type { CSSProperties, JSX } from "react";
import { DISPLAY_HEADING_CLASS, DISPLAY_RULE_CLASS } from "../ui/display-heading";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { SectionStyle } from "../section-style.fields";
import { cn } from "../../lib/utils";

interface ContentBlockProps {
  id?: string;
  className?: string;
  heading: string;
  content: string;
  imageUrl?: string;
  imageAlt?: string;
  layout: "image-left" | "image-right" | "centered" | "no-image" | "statement";
  /** Brand is a solid band in the site's primary colour, with the text in its foreground colour. */
  backgroundColor?: "white" | "gray" | "primary" | "brand";
  headingSize?: "large" | "medium" | "small";
  /** Framing for the image. "none" is right for logos and marks that sit on the page. */
  imageStyle?: "shadow" | "border" | "none";
  /** White turns a one-colour logo white, for a brand band or a dark background. */
  imageColor?: "original" | "white";
  /** How much of the row the image takes beside the text, or how wide it is when centred, as a percent. */
  imageSize?: number;
  /** Bold: the heading in display capitals, to match the page's other bold sections. */
  variant?: SectionStyle;
}

/**
 * The image's alt text: the pro's own, else the heading when it is plain text.
 * @param {string | undefined} imageAlt - The alt text the pro set, if any.
 * @param {string} heading - The section heading.
 * @returns {string} The alt text.
 */
const imageAltText = (imageAlt: string | undefined, heading: string): string => {
    if (imageAlt !== undefined && imageAlt !== "") {
      return imageAlt;
    }
    if (typeof heading === "string") {
      return heading;
    }
    return "";
  },
  /**
   * Explicit White must stay white: the inline style outranks the section
   * alternation rule in index.css, which keys on the bg-background class.
   * @param {ContentBlockProps["backgroundColor"]} backgroundColor - The section's background choice.
   * @returns {CSSProperties | undefined} The inline style, or undefined for the other colors.
   */
  sectionStyle = (backgroundColor: ContentBlockProps["backgroundColor"]): CSSProperties | undefined => {
    if (backgroundColor === "white") {
      return { backgroundColor: "var(--jobsite-background)" };
    }
    return undefined;
  },
  /**
   * Tolerate a ReactNode in `content`/`heading`: the editors swap the string
   * for an inline-editable element. Strings keep the exact old semantics
   * (blank-line paragraph split); an element renders as one block.
   * @param {string} content - The section's text, or the editor's element in its place.
   * @returns {JSX.Element | JSX.Element[]} One paragraph per blank-line-separated block.
   */
  renderProse = (content: string): JSX.Element | JSX.Element[] => {
    if (typeof content === "string") {
      return content.split("\n\n").filter(Boolean).map((paragraph, idx) => <p key={idx}>{paragraph}</p>);
    }
    return <p>{content}</p>;
  },
  ContentBlock = ({
    id,
    className,
    heading,
    content,
    imageUrl,
    imageAlt,
    layout = "image-left",
    backgroundColor = "white",
    headingSize = "large",
    imageStyle = "shadow",
    imageColor = "original",
    imageSize,
    variant = "standard",
  }: ContentBlockProps): JSX.Element => {
    const isBold = variant === "bold",
      bgColorClasses = {
        gray: "jobsite:bg-muted/50",
        brand:
          // The text classes inside name the page's foreground; on the band they take its foreground instead.
          "jobsite:bg-primary jobsite:text-primary-foreground jobsite:[&_h2]:text-primary-foreground jobsite:[&_p]:text-primary-foreground/90",
        primary: "jobsite:bg-primary/5",
        white: "jobsite:bg-background",
      },

      headingSizeClasses = {
        large: "jobsite:text-3xl jobsite:sm:text-4xl",
        medium: "jobsite:text-2xl jobsite:sm:text-3xl",
        small: "jobsite:text-xl jobsite:sm:text-2xl",
      },

      showImage = layout !== "no-image" && layout !== "statement" && imageUrl !== undefined && imageUrl !== "",
      surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,

      imageElement = showImage && (
        <img
          crossOrigin="anonymous"
          src={imageUrl}
          alt={imageAltText(imageAlt, heading)}
          className={cn(
            // A centred image with no framing is a logo or a mark: logo-sized, not full width.
            layout === "centered" && imageStyle === "none" && imageSize === undefined ? "jobsite:mx-auto jobsite:h-20 jobsite:w-auto jobsite:md:h-24" : "jobsite:w-full jobsite:h-auto",
            imageColor === "white" && "jobsite:brightness-0 jobsite:invert",
            imageStyle === "border" && "jobsite:border jobsite:border-border",
            imageStyle === "shadow" && "jobsite:shadow-lg",
          )}
          style={surfaceRadiusStyle}
        />
      ),

      /**
       * The bold heading: an accent rule, then the heading in display capitals.
       * @param {boolean} centered Whether the layout centres it.
       * @returns {JSX.Element} The heading.
       */
      renderBoldHeading = (centered: boolean): JSX.Element => (
        <>
          <span aria-hidden="true" className={cn(DISPLAY_RULE_CLASS, centered && "jobsite:mx-auto")} />
          <h2 className={cn(DISPLAY_HEADING_CLASS, "jobsite:mb-6 jobsite:text-foreground")}>{heading}</h2>
        </>
      ),

      renderHeading = (): JSX.Element => {
        if (isBold) {
          return renderBoldHeading(layout === "centered");
        }
        return (
          <h2
            className={cn(
              "jobsite:font-heading jobsite:mb-5 jobsite:text-foreground",
              headingSizeClasses[headingSize],
            )}
          >
            {heading}
          </h2>
        );
      },

      // Editorial statement: the heading is the display element, prose answers it
      // Alongside. The heading font and colors come from the theme, so the same
      // Layout reads corporate in a sans theme and luxurious in a serif one.
      renderStatement = (): JSX.Element => (
        <div
          className={
            isBold
              ? "jobsite:grid jobsite:gap-2 jobsite:lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] jobsite:lg:gap-16"
              : "jobsite:mx-auto jobsite:grid jobsite:max-w-6xl jobsite:gap-8 jobsite:md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] jobsite:md:gap-16"
          }
        >
          {isBold ? <div>{renderBoldHeading(false)}</div> : (
          <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-5xl">
            {heading}
          </h2>
          )}
          <div className={cn("jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground jobsite:md:pt-2", isBold && "jobsite:max-w-2xl jobsite:md:pt-0 jobsite:lg:pt-6")}>
            {renderProse(content)}
          </div>
        </div>
      ),

      renderCentered = (): JSX.Element => (
        <div className="jobsite:max-w-3xl jobsite:mx-auto jobsite:text-center">
          {showImage && (
            <div className="jobsite:mx-auto jobsite:mb-10" style={imageSize === undefined ? undefined : { width: `${imageSize}%` }}>
              {imageElement}
            </div>
          )}
          {renderHeading()}
          <div className="jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground">
            {renderProse(content)}
          </div>
        </div>
      ),

      renderSideBySide = (): JSX.Element => (
        <div
          className={cn(
            "jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-8 jobsite:md:gap-16",
            layout === "image-right" && "jobsite:md:flex-row-reverse",
            layout !== "image-right" && "jobsite:md:flex-row",
          )}
        >
          {showImage && (
            <div
              className={imageSize === undefined ? "jobsite:w-full jobsite:md:w-1/2" : "jobsite:w-full jobsite:md:w-auto jobsite:md:shrink-0"}
              // Flex basis sets the width in the desktop row and does nothing in the phone's stacked column.
              style={imageSize === undefined ? undefined : { flexBasis: `${imageSize}%` }}
            >
              {imageElement}
            </div>
          )}
          <div
            className={cn(
              showImage && (imageSize === undefined ? "jobsite:w-full jobsite:md:w-1/2" : "jobsite:w-full jobsite:md:min-w-0 jobsite:md:flex-1"),
              !showImage && "jobsite:w-full jobsite:max-w-3xl jobsite:mx-auto",
              // Bold sets its text on the page's one left edge, like its other sections.
              !showImage && isBold && "jobsite:mx-0 jobsite:self-start",
            )}
          >
            {renderHeading()}
            <div className="jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground">
              {renderProse(content)}
            </div>
          </div>
        </div>
      ),

      renderLayout = (): JSX.Element => {
        if (layout === "statement") {
          return renderStatement();
        }
        if (layout === "centered") {
          return renderCentered();
        }
        return renderSideBySide();
      };

    return (
      <section
        id={id}
        className={cn("jobsite:py-16 jobsite:md:py-24", bgColorClasses[backgroundColor], className)}
        style={sectionStyle(backgroundColor)}
      >
        <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
          {renderLayout()}
        </div>
      </section>
    );
  };

export type { ContentBlockProps };
export { ContentBlock };
